'use client'
import { ReactNode, useState } from "react";
import { usePendingCallsContext } from "./PendingCallsContext";
import { CallingCardUIComponent } from "./CallingCardUIComponent";
import { usePathname } from "next/navigation";
import { EndCallModal, EndCallModalDialogRoot } from "../../Modals/endCallModal/EndCallModal";
import { Toast } from "@umahealth/occipital";
import Link from "next/link";

const examSegments = ['examen-salud', 'examen-sustancias', 'observacion-general', 'resumen'];

export function CallingCard({ children }: { children: ReactNode }) {
	const { calls, dismissCall } = usePendingCallsContext();
	const pathName = usePathname();
	const [dismissedToasts, setDismissedToasts] = useState<Set<string>>(new Set());

	const isOnVideollamada = pathName.includes("videollamada");
	const isOnExam = examSegments.some(s => pathName.includes(s));

	if (isOnVideollamada) {
		return <>{children}</>;
	}

	if (isOnExam) {
		return (
			<div data-testid="exam-toast-container">
				{calls.map((call) => {
					if (dismissedToasts.has(call.id)) return null;
					const description = call.status === "finished"
						? `Revision finalizada${call.apto !== undefined ? (call.apto ? " - APTO" : " - NO APTO") : ""}`
						: call.inCall
							? `Dr. ${call.doctorName} te llama`
							: `Dr. ${call.doctorName} esta revisando una ficha`;

					return (
						<Toast
							key={call.id}
							description={description}
							open={true}
							onOpenChange={() => {
								setDismissedToasts((prev) => new Set(prev).add(call.id));
								if (call.status === "finished") dismissCall(call.id);
							}}
							duration={5000}
							type={call.status === "finished" ? (call.apto ? "success" : "warning") : "info"}
						/>
					);
				})}
				{children}
			</div>
		);
	}

	const inProgressCalls = calls.filter(c => c.status === "in-progress" && c.inCall);
	const reviewingCalls = calls.filter(c => c.status === "in-progress" && !c.inCall);
	const finishedCalls = calls.filter(c => c.status === "finished");

	return (
		<>
			{/*
			  * El aviso cerrado se recuerda igual que el de los otros toasts, con
			  * `dismissedToasts` y no con `dismissCall`: cerrar el cartel esconde el
			  * aviso, no descarta la llamada. La llamada sigue en la lista de
			  * pendientes de Evaluaciones, con su botón de Atender.
			  */}
			{inProgressCalls.map((call) => {
				if (dismissedToasts.has(call.id)) return null;
				return (
					<CallingCardUIComponent
						key={call.id}
						doctorName={call.doctorName}
						href={`/operaciones/crear/${call.operationId}/videollamada`}
						onClose={() =>
							setDismissedToasts((prev) => new Set(prev).add(call.id))
						}
					/>
				);
			})}
			{reviewingCalls.map((call) => {
				if (dismissedToasts.has(call.id)) return null;
				return (
					<Toast
						key={call.id}
						description={`Un doctor empezo a revisar una ficha.`}
						open={true}
						onOpenChange={() => {
							setDismissedToasts((prev) => new Set(prev).add(call.id));
						}}
						duration={1000}
						type="info"
					/>
				);
			})}
			{finishedCalls.length > 0 && (
				<EndCallModalDialogRoot defaultOpen>
					<EndCallModal
						approved={finishedCalls[0].apto}
						fullname={finishedCalls[0].operator ? `${finishedCalls[0].operator.firstName} ${finishedCalls[0].operator.lastName}` : undefined}
						legajo={finishedCalls[0].operator?.nomina}
						operationId={finishedCalls[0].operationId}
						uid={finishedCalls[0].operator?.uid}
						companyId={finishedCalls[0].operator?.base?.company_id}
						baseUrl={typeof window !== 'undefined' ? window.location.origin : ''}
					/>
				</EndCallModalDialogRoot>
			)}
			{children}
		</>
	);
}
